import {Component, input, OnDestroy, output} from '@angular/core';
import {
  AbstractControl,
  AsyncValidator, ValidatorFn, AsyncValidatorFn,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  TouchedChangeEvent,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {VehicleControlUnit, VehicleHardwareType} from '../model/plowops-pro-models';
import {AvlModel, DeviceListItemBasic, VehicleModel} from '../../../../../../shared/models/vehicle.model';
import {BehaviorSubject, firstValueFrom, interval, Observable, of, Subscription, switchMap} from 'rxjs';
import {debounceTime, filter, map, take, tap} from 'rxjs/operators';
import {VehiclesService} from '../../../../../../data/vehicles/vehicles.service';
import {MatFormField, MatInput, MatSuffix} from '@angular/material/input';
import {InstantErrorStateMatcherInstance} from '../../../../../../shared/tools/InstantErrorStateMatcher';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatError} from '@angular/material/form-field';
import {
  EditVehicleHardwareDialogComponent,
  EditVehicleHardwareDialogData,
  EditVehicleType
} from '../edit-vehicle-hardware-dialog/edit-vehicle-hardware-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {ToastService} from '../../../../../../shared/services/toast.service';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatDivider, MatListItem, MatListItemMeta, MatListItemTitle, MatNavList} from '@angular/material/list';
import {MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatIcon} from '@angular/material/icon';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatAutocomplete, MatAutocompleteOrigin, MatAutocompleteTrigger} from '@angular/material/autocomplete';

@Component({
  selector: 'app-vehicle-control-unit',
  imports: [
    ReactiveFormsModule,
    MatInput,
    MatFormField,
    MatSelect,
    MatOption,
    MatError,
    MatButton,
    MatNavList,
    MatListItem,
    MatMenu,
    MatMenuTrigger,
    MatIcon,
    MatIconButton,
    MatMenuContent,
    MatMenuItem,
    MatListItemMeta,
    MatListItemTitle,
    MatSuffix,
    MatProgressSpinner,
    MatDivider,
    MatAutocompleteOrigin,
    MatAutocompleteTrigger,
    MatAutocomplete
  ],
  templateUrl: './vehicle-control-unit.component.html',
  styleUrls: [
    './vehicle-control-unit.component.scss',
    '../../../../settings-fields.scss',
    '../../../../settings-common.scss'
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: VehicleControlUnitComponent
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      multi: true,
      useExisting: VehicleControlUnitComponent
    }
  ],
})
export class VehicleControlUnitComponent implements ControlValueAccessor, AsyncValidator, OnDestroy {
  vehicleId = input<number>();

  vehicleChanged = output<VehicleModel>();

  vcu: VehicleControlUnit;

  formGroup: FormGroup;

  subscriptions: Subscription[] = [];

  vehicleHardwareTypeLabels: { [key in VehicleHardwareType]: string } = {
    [VehicleHardwareType.PLOW]: 'Plow',
    [VehicleHardwareType.GRANULAR_SPREADER]: 'Granular Spreader',
    [VehicleHardwareType.LIQUID_SPREADER]: 'Liquid Spreader',
    [VehicleHardwareType.SWEEPER]: 'Sweeper',
    [VehicleHardwareType.MOWER]: 'Mower',
  };
  protected readonly allAvlModels = [
    {id: AvlModel.NONE, name: 'None'},
    {id: AvlModel.PLOWOPS_PROHUB, name: 'PlowOps ProHub'},

    {id: AvlModel.PLOWOPS_HUB_C, name: 'PlowOps Hub (Rev C)'},
    {id: AvlModel.CALAMP_2630, name: 'CalAmp 2630'},
    {id: AvlModel.CALAMP_3641, name: 'CalAmp 3641'},
    {id: AvlModel.CALAMP_5530, name: 'CalAmp 5530'}
  ];
  protected readonly InstantErrorStateMatcherInstance = InstantErrorStateMatcherInstance;
  protected readonly ListItemType = ListItemType;
  protected readonly VehicleHardwareType = VehicleHardwareType;

  protected proHubSerialValuesLoading = false;
  protected availableProHubSerialOptions: DeviceListItemBasic[] = [];
  protected hasMoreProHubSerialOptions = false;
  private currentProHubSerialOptionsPage = 0;
  private lastSearchProHubSerialValue: string | undefined;
  protected readonly AvlModel = AvlModel;
  private readonly proHubSerialValue = new BehaviorSubject('');

  // Store the initial validators so you can restore them
  private controlsValidators: { [key: string]: ValidatorFn[] } = {
    avlModel: [Validators.required],
    lmuId: [Validators.required],
    proHubSerial: [Validators.required]
  };
  private controlsAsyncValidators: { [key: string]: AsyncValidatorFn[] } = {
    lmuId: [this.validateValueUniqueness('lmuId', 'lmuIdTaken')],
  };

  constructor(fb: FormBuilder,
              private readonly vehiclesService: VehiclesService,
              private readonly dialog: MatDialog,
              private readonly toastService: ToastService) {
    this.formGroup = fb.group({
      avlModel: fb.control(AvlModel.NONE),
      lmuId: fb.control(null, {
        updateOn: 'blur',
      }),
      proHubSerial: fb.control(null),
    });

    this.subscriptions.push(
      this.formGroup.events.pipe(
        filter(e => e instanceof TouchedChangeEvent && e.touched)
      ).subscribe(() => this.onTouchedListener())
    );

    this.subscriptions.push(
      this.formGroup.valueChanges.subscribe(value => {
        const {avlModel, lmuId, proHubSerial} = value;

        this.vcu.avlModel = avlModel;
        this.vcu.lmuId = avlModel === AvlModel.PLOWOPS_PROHUB ? proHubSerial : lmuId;
        this.onChangeListener(this.vcu);
      })
    );

    this.subscriptions.push(
      this.formGroup.controls.avlModel.valueChanges.subscribe(value => this.handleAvlModelValue(value))
    );

    this.subscriptions.push(
      this.proHubSerialValue.pipe(
        debounceTime(200),
        tap(value => {
          this.proHubSerialValuesLoading = true;
          this.currentProHubSerialOptionsPage = 0;
          this.lastSearchProHubSerialValue = value;
        }),
        switchMap(value => this.vehiclesService.listAvailableImeis(this.vehicleId(), 0, value))
      ).subscribe(result => {
        this.availableProHubSerialOptions = result.content;
        this.hasMoreProHubSerialOptions = result.totalPages > 1;
        this.proHubSerialValuesLoading = false;
      })
    );
  }

  loadMoreProHubSerialOptions(event: MouseEvent) {
    event.stopPropagation();

    const nextPage = this.currentProHubSerialOptionsPage + 1;
    this.proHubSerialValuesLoading = true;

    this.vehiclesService
      .listAvailableImeis(this.vehicleId(), nextPage, this.lastSearchProHubSerialValue)
      .subscribe(result => {
        this.availableProHubSerialOptions = [
          ...this.availableProHubSerialOptions,
          ...result.content
        ];
        this.currentProHubSerialOptionsPage = nextPage;
        this.hasMoreProHubSerialOptions = result.totalPages > nextPage + 1;
        this.proHubSerialValuesLoading = false;
      });
  }

  /**
   * Toggles the disabled state of a control and manages its validators.
   * @param controlName The name of the control to toggle.
   * @param disable If true, the control is disabled and validators are cleared.
   *        If false, the control is enabled and validators are restored.
   */
  private toggleControl(controlName: string, disable: boolean): void {
    const control = this.formGroup.get(controlName);
    if (!control) {
      // console.warn(`Control '${controlName}' not found.`);
      return;
    }
    if (disable) {
      // 1. Clear Validators: This removes validation errors from the control's status.
      control.clearValidators();
      control.clearAsyncValidators();
      // 2. Disable: Makes it unresponsive to user input and excludes the value.
      control.disable();
    } else {
      // 1. Restore Validators: Re-apply the initial validators.
      const validators = this.controlsValidators[controlName];
      if (validators?.length > 0) {
        control.setValidators(validators);
      }
      const asyncValidators = this.controlsAsyncValidators[controlName];
      if (asyncValidators?.length > 0) {
        control.setAsyncValidators(asyncValidators);
      }
      // 2. Enable: Makes it responsive to user input.
      control.enable();
    }

    // 3. Recalculate validity and propagate the change up the form tree.
    control.updateValueAndValidity();
    // Also, update the form group's status for the nested component itself
    this.formGroup.updateValueAndValidity();
  }

  proHubSerialChanged(event: any) {
    this.proHubSerialValue.next(event.target.value);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  writeValue(obj: VehicleControlUnit) {
    this.vcu = obj || new VehicleControlUnit();
    const {avlModel, lmuId} = this.vcu;
    this.formGroup.setValue({
      avlModel: avlModel || null,
      lmuId: lmuId || null,
      proHubSerial: lmuId || null
    }, {
      emitEvent: false
    });
  }

  onChangeListener = _ => {
  };

  onTouchedListener = () => {
  };

  onValidatorChange = () => {
  };

  registerOnChange(fn: any) {
    this.onChangeListener = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedListener = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.formGroup.disable({emitEvent: false});
    } else {
      this.formGroup.enable({emitEvent: false});
    }
  }

  registerOnValidatorChange(fn: () => void) {
    this.onValidatorChange = fn;
  }

  validate(control: FormControl): Observable<ValidationErrors | null> {
    return interval(100)
      .pipe(
        map(() => this.formGroup.status),
        filter(status => (status === 'VALID' || status === 'INVALID')),
        take(1),
        map(status => status === 'INVALID' ? {_control: {error: true}} : null),
      );
  }

  addVehicleHardware() {
    const ref = this.dialog
      .open<EditVehicleHardwareDialogComponent, EditVehicleHardwareDialogData>(EditVehicleHardwareDialogComponent, {
        data: {
          vehicleId: this.vehicleId()
        },
        minWidth: '600px'
      });

    firstValueFrom(ref.afterClosed()).then(vehicle => {
      if (vehicle) {
        this.toastService.long('Vehicle hardware has been added');
        this.vehicleChanged.emit(vehicle);
      }
    });
  }

  editHardware(type: ListItemType, additionalHardwareIndex?: any) {
    const editedType = {
      [ListItemType.CAMERA]: EditVehicleType.CAMERA,
      [ListItemType.DIGITAL_SPREADER]: EditVehicleType.DIGITAL_SPREADER,
      [ListItemType.OTHER]: EditVehicleType.OTHER,
    }[type];
    const ref = this.dialog
      .open<EditVehicleHardwareDialogComponent, EditVehicleHardwareDialogData>(EditVehicleHardwareDialogComponent, {
        data: {
          vehicleId: this.vehicleId(),
          editedType,
          editedIndex: additionalHardwareIndex
        },
        width: '600px'
      });

    firstValueFrom(ref.afterClosed()).then(vehicle => {
      if (vehicle) {
        this.toastService.long('Vehicle hardware configuration has been updated');
        this.vehicleChanged.emit(vehicle);
      }
    });
  }

  removeHardware(type: ListItemType, additionalHardwareIndex?: any) {
    this.onTouchedListener();
    switch (type) {
      case ListItemType.CAMERA:
        delete this.vcu.cameraConfiguration;
        this.toastService.long('Camera has been removed');
        break;
      case ListItemType.DIGITAL_SPREADER:
        delete this.vcu.digitalSpreader;
        this.toastService.long('Digital spreader has been removed');
        break;
      case ListItemType.OTHER: {
        const [removedHardware] = this.vcu.additionalHardware.splice(additionalHardwareIndex, 1);
        this.toastService.long(`${this.vehicleHardwareTypeLabels[removedHardware.type]} has been removed`);
        break;
      }
    }
    this.onChangeListener(this.vcu);
  }

  private handleAvlModelValue(value?: AvlModel) {
    const {lmuId, proHubSerial} = this.formGroup.controls;
    if (value === AvlModel.PLOWOPS_PROHUB) {
      this.proHubSerialValue.next('');
      proHubSerial.setValue(null, {emitEvent: false});
      this.toggleControl('proHubSerial', false);
      this.toggleControl('lmuId', true);

    } else if (value !== AvlModel.NONE && value !== AvlModel.PLOWOPS_HUB_C) {
      lmuId.setValue(null, {emitEvent: false});
      this.toggleControl('proHubSerial', true);
      this.toggleControl('lmuId', false);

    } else {
      this.toggleControl('proHubSerial', true);
      this.toggleControl('lmuId', true);
    }
  }

  private validateValueUniqueness(fieldName: string, resultFieldName: string) {
    return (control: AbstractControl) => {
      const {value, pristine} = control;

      if (!value || pristine) {
        return of(null);
      }
      return this.vehiclesService.checkVehicleDataUniqueness({
        id: this.vehicleId(),
        [fieldName]: value
      } as VehicleModel).pipe(
        map(result => result[resultFieldName] ? {notUnique: true} as ValidationErrors : null),
      );
    };
  }
}

export enum ListItemType {
  CAMERA = 'CAMERA',
  DIGITAL_SPREADER = 'DIGITAL_SPREADER',
  OTHER = 'OTHER',
}
